import { useState } from 'react'
import { PencilIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { RiskReductionMeasure } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { Switch } from '@/components/ui/switch'
import { EditRiskReductionMeasureDescriptionDialog } from './EditRiskReductionMeasureDescriptionDialog'
import { EditRiskReductionMeasureTypeDialog } from './EditRiskReductionMeasureTypeDialog'
import { RiskReductionMeasurePlrDisplay } from './RiskReductionMeasurePlrDisplay'
import type { RiskReductionMeasureFormData } from './RiskReductionMeasureForm.schema'
import {
  isSafetyFunctionMeasure,
  toRiskReductionMeasureFormData,
} from './riskReductionMeasuresUtils'
import { getRrmStandardProvenanceLabel } from './getRrmStandardProvenanceLabel'

type RiskReductionMeasureDetailInfoWidgetsProps = {
  riskReductionMeasure: RiskReductionMeasure
  isUpdating: boolean
  onUpdate: (data: RiskReductionMeasureFormData, onSuccess?: () => void) => void
}

function getMeasureTypeLabel(
  measureType: RiskReductionMeasure['type'],
  translate: (key: string) => string
) {
  return translate(
    `riskReductionMeasuresLibraryPage.form.measureTypes.${measureType}`
  )
}

function getSourceLabel(
  riskReductionMeasure: RiskReductionMeasure,
  translate: TFunction<'machineryDirectiveCompliance'>
) {
  if (riskReductionMeasure.source === 'STANDARD') {
    const provenanceLabel = getRrmStandardProvenanceLabel(
      riskReductionMeasure,
      translate
    )
    if (provenanceLabel != null) {
      return provenanceLabel
    }
    return translate('riskReductionMeasuresLibraryPage.source.standard')
  }
  return translate('riskReductionMeasuresLibraryPage.source.user')
}

export function RiskReductionMeasureDetailInfoWidgets({
  riskReductionMeasure,
  isUpdating,
  onUpdate,
}: RiskReductionMeasureDetailInfoWidgetsProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false)
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false)
  const naLabel = t('hazardsPage.hazardDetail.na')

  let description = naLabel
  if (
    riskReductionMeasure.description != null &&
    riskReductionMeasure.description.trim().length > 0
  ) {
    description = riskReductionMeasure.description
  }
  const showDependsOnControlSystem = riskReductionMeasure.type === 'SCP'
  const dependsOnControlSystem =
    riskReductionMeasure.dependsOnControlSystem === true
  const showPlr = isSafetyFunctionMeasure(riskReductionMeasure)

  const handleTypeSave = (data: RiskReductionMeasureFormData) => {
    onUpdate(data, () => {
      setIsTypeDialogOpen(false)
    })
  }

  const handleDescriptionSave = (data: RiskReductionMeasureFormData) => {
    onUpdate(data, () => {
      setIsDescriptionDialogOpen(false)
    })
  }

  const handleDependsOnControlSystemChange = (checked: boolean) => {
    const base = toRiskReductionMeasureFormData(riskReductionMeasure)
    onUpdate({
      ...base,
      dependsOnControlSystem: checked,
      safetyFunctionId: checked ? base.safetyFunctionId : null,
    })
  }

  return (
    <>
      <div className='grid gap-2 sm:grid-cols-2'>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>
              {t('riskReductionMeasuresLibraryPage.form.type')}
            </ItemTitle>
            <ItemDescription>
              {getMeasureTypeLabel(riskReductionMeasure.type, t)}
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8 shrink-0 text-muted-foreground'
              onClick={() => setIsTypeDialogOpen(true)}
              disabled={isUpdating}
              aria-label={t(
                'riskReductionMeasuresLibraryPage.rrmDetail.editTypeAria'
              )}
            >
              <PencilIcon className='size-4' />
            </Button>
          </ItemActions>
        </Item>

        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>
              {t('riskReductionMeasuresLibraryPage.rrmDetail.sourceLabel')}
            </ItemTitle>
            <ItemDescription>
              {getSourceLabel(riskReductionMeasure, t)}
            </ItemDescription>
          </ItemContent>
        </Item>

        {showDependsOnControlSystem ? (
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>
                {t(
                  'riskReductionMeasuresLibraryPage.rrmDetail.dependsOnControlSystemLabel'
                )}
              </ItemTitle>
              <ItemDescription>
                {dependsOnControlSystem
                  ? t('riskReductionMeasuresLibraryPage.form.yes')
                  : t('riskReductionMeasuresLibraryPage.form.no')}
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <Switch
                checked={dependsOnControlSystem}
                onCheckedChange={handleDependsOnControlSystemChange}
                disabled={isUpdating}
                aria-label={t(
                  'riskReductionMeasuresLibraryPage.rrmDetail.dependsOnControlSystemLabel'
                )}
              />
            </ItemActions>
          </Item>
        ) : null}

        {showPlr ? (
          <RiskReductionMeasurePlrDisplay
            riskReductionMeasure={riskReductionMeasure}
            variant='detail'
          />
        ) : null}

        <Item variant='outline' size='xs' className='sm:col-span-2'>
          <ItemContent>
            <ItemTitle>
              {t('riskReductionMeasuresLibraryPage.form.description')}
            </ItemTitle>
            <ItemDescription className='whitespace-pre-wrap'>
              {description}
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8 shrink-0 text-muted-foreground'
              onClick={() => setIsDescriptionDialogOpen(true)}
              disabled={isUpdating}
              aria-label={t(
                'riskReductionMeasuresLibraryPage.rrmDetail.editDescriptionAria'
              )}
            >
              <PencilIcon className='size-4' />
            </Button>
          </ItemActions>
        </Item>
      </div>

      <EditRiskReductionMeasureTypeDialog
        isOpen={isTypeDialogOpen}
        onClose={() => setIsTypeDialogOpen(false)}
        riskReductionMeasure={riskReductionMeasure}
        onSave={handleTypeSave}
        isSaving={isUpdating}
      />

      <EditRiskReductionMeasureDescriptionDialog
        isOpen={isDescriptionDialogOpen}
        onClose={() => setIsDescriptionDialogOpen(false)}
        riskReductionMeasure={riskReductionMeasure}
        onSave={handleDescriptionSave}
        isSaving={isUpdating}
      />
    </>
  )
}
